import { useRef } from "react";
import { Icon, IconButton, useDialogBehavior } from "@astrum/ui";
import { useFileUrl } from "../lib/useFileUrl.js";
import "./image-lightbox.css";

/**
 * A full-size view of an image attachment, opened from its thumbnail.
 *
 * Not built on Modal: that component is a padded, width-capped content card,
 * which is the wrong shape for an image that should fill as much of the
 * viewport as it can. This borrows the same focus-trap/Escape/backdrop-click
 * behavior Modal and Sheet share, in a layout suited to a picture instead.
 */
export function ImageLightbox({
  fileId,
  fileName,
  open,
  onClose,
}: {
  fileId: string;
  fileName: string;
  open: boolean;
  onClose: () => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const src = useFileUrl(open ? `/api/v1/files/${fileId}/preview` : null);

  useDialogBehavior(open, onClose, containerRef);

  if (!open) return null;

  return (
    <div
      className="image-lightbox"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={containerRef}
        className="image-lightbox__frame"
        role="dialog"
        aria-modal="true"
        aria-label={fileName}
        tabIndex={-1}
      >
        <IconButton
          className="image-lightbox__close"
          icon={<Icon name="close" size={18} />}
          label="Close"
          onClick={onClose}
        />
        {src ? (
          <img src={src} alt={fileName} className="image-lightbox__image" />
        ) : (
          <div className="image-lightbox__loading" aria-hidden="true" />
        )}
        <p className="image-lightbox__caption">{fileName}</p>
      </div>
    </div>
  );
}
